import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Quagga from '@ericblade/quagga2';
import { adminAPI } from '../../services/api';
import './AdminPages.css';

const BARCODE_SCANNER_ELEMENT_ID = 'barcode-scanner-root';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

const SparePartsAdmin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category'); // 'accessories' = صفحة الكماليات
  const isAccessoriesPage = category === 'accessories';
  const [spareParts, setSpareParts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSparePart, setEditingSparePart] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterVariant, setFilterVariant] = useState('');
  const filterPartType = filterArea; // legacy alias (replaced by area filter)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({ count: 0, total_pages: 0, next: null, previous: null });
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeMode, setBarcodeMode] = useState('manual'); // 'manual' | 'camera'
  const [cameraError, setCameraError] = useState(null);
  const [lastScannedCode, setLastScannedCode] = useState(''); // للعرض فقط
  const barcodeProcessedRef = useRef(false);
  const quaggaDetectedRef = useRef(null);
  const lastDecodedCodeRef = useRef(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);

  useEffect(() => {
    if (!showBarcodeModal || barcodeMode !== 'camera') return;
    const el = document.getElementById(BARCODE_SCANNER_ELEMENT_ID);
    if (!el) return;
    setCameraError(null);
    lastDecodedCodeRef.current = null;

    const onDetected = (data) => {
      const code = (data?.codeResult?.code ?? '').toString().trim();
      if (!code || code.length < 2 || barcodeProcessedRef.current) return;
      setLastScannedCode(code);
      if (lastDecodedCodeRef.current === code) {
        applyBarcodeResult(code);
        Quagga.stop();
        return;
      }
      lastDecodedCodeRef.current = code;
    };
    quaggaDetectedRef.current = onDetected;

    Quagga.init(
      {
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: el,
          constraints: { facingMode: 'environment', width: 640, height: 480 },
        },
        locator: { patchSize: 'medium', halfSample: true },
        numOfWorkers: navigator.hardwareConcurrency ? Math.min(4, navigator.hardwareConcurrency) : 2,
        frequency: 10,
        decoder: {
          readers: ['ean_reader', 'code_128_reader', 'code_39_reader', 'upc_reader', 'upc_e_reader', 'ean_8_reader'],
        },
        locate: true,
      },
      (err) => {
        if (err) {
          console.error('Quagga init error:', err);
          setCameraError(err?.message || 'تعذر الوصول إلى الكاميرا. تأكد من منح إذن الكاميرا للموقع.');
          return;
        }
        Quagga.onDetected(onDetected);
        Quagga.start();
      }
    );

    return () => {
      Quagga.offDetected(quaggaDetectedRef.current);
      Quagga.stop();
    };
  }, [showBarcodeModal, barcodeMode]);

  const AREA_OPTIONS = [{ value: 'front', label: 'أمامي' }, { value: 'rear', label: 'خلفي' }, { value: 'side', label: 'جانبي' }, { value: 'interior', label: 'الداخلية' }];
  const [formData, setFormData] = useState({
    area: 'front',
    variant_ids: [],
    name: '',
    name_ar: '',
    part_number: '',
    quantity: 0,
    price: '',
    description: '',
    in_stock: true,
    is_active: true,
    is_accessory: false,
    image: null,
    extra_images: [],
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [extraImagesPreviews, setExtraImagesPreviews] = useState([]);
  const [addingExtraImage, setAddingExtraImage] = useState(false);
  const [variantSearchQuery, setVariantSearchQuery] = useState('');
  const extraImagesInputRef = useRef(null);
  const [variantDropdownOpen, setVariantDropdownOpen] = useState(false);

  const fetchSpareParts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (filterArea) params.area = filterArea;
      if (filterVariant) params.variant = filterVariant;
      const response = isAccessoriesPage
        ? await adminAPI.getAccessories(params)
        : await adminAPI.getSpareParts(params);
      const data = response.data;
      if (Array.isArray(data)) {
        setSpareParts(data);
        setPagination({ count: data.length, total_pages: 1, next: null, previous: null });
      } else {
        setSpareParts(data.results || []);
        setPagination({
          count: data.count ?? 0,
          total_pages: Math.ceil((data.count || 0) / pageSize) || 1,
          next: data.next,
          previous: data.previous,
        });
      }
    } catch (err) {
      console.error('Error fetching spare parts:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || 'فشل تحميل قطع الغيار';
      alert(errorMessage);
      setSpareParts([]);
      setPagination({ count: 0, total_pages: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filterArea, filterVariant, category, isAccessoriesPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchVariants();
  }, []);

  useEffect(() => {
    if (location.state?.openAddForm) {
      if (isAccessoriesPage) setFormData((prev) => ({ ...prev, is_accessory: true }));
      setEditingSparePart(null);
      setShowForm(true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state?.openAddForm, location.pathname, location.search, navigate, isAccessoriesPage]);

  useEffect(() => {
    if (reorderMode) return;
    fetchSpareParts();
  }, [fetchSpareParts, reorderMode]);

  const fetchAllForReorder = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: 1, page_size: 2000 };
      const response = isAccessoriesPage
        ? await adminAPI.getAccessories(params)
        : await adminAPI.getSpareParts(params);
      const data = response.data;
      let list = Array.isArray(data) ? data : data.results || [];
      list = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name_ar || a.name || '').localeCompare(b.name_ar || b.name || ''));
      setSpareParts(list);
      setPagination({ count: list.length, total_pages: 1, next: null, previous: null });
      if (list.length >= 2000) {
        alert('وصلت القائمة إلى الحد الأقصى (2000). رتّب ثم احفظ؛ إن كان لديك أكثر من ذلك راجع المطور لزيادة الحد.');
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.response?.data?.detail || 'فشل تحميل القائمة للترتيب');
    } finally {
      setLoading(false);
    }
  }, [isAccessoriesPage]);

  useEffect(() => {
    if (!reorderMode) return;
    fetchAllForReorder();
  }, [reorderMode, fetchAllForReorder]);

  const fetchVariants = async () => {
    try {
      const response = await adminAPI.getCarVariants();
      const data = response.data;
      if (Array.isArray(data)) {
        setVariants(data);
      } else if (data && data.results && Array.isArray(data.results)) {
        setVariants(data.results);
      } else {
        setVariants([]);
      }
    } catch (err) {
      console.error('Error fetching variants:', err);
      setVariants([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'extra_images' && files && files.length) {
      const fileList = Array.from(files);
      setFormData(prev => ({ ...prev, extra_images: [...(prev.extra_images || []), ...fileList] }));
      setExtraImagesPreviews((prev) => [...prev, ...fileList.map((f) => URL.createObjectURL(f))]);
      e.target.value = '';
      return;
    }
    const file = (e.target.files && e.target.files[0]) || null;
    setFormData(prev => ({ ...prev, image: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleAddExtraImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !editingSparePart) return;
    setAddingExtraImage(true);
    try {
      const res = isAccessoriesPage
        ? await adminAPI.addAccessoryImage(editingSparePart.id, file)
        : await adminAPI.addSparePartImage(editingSparePart.id, file);
      setEditingSparePart(res.data);
      setSpareParts(prev => prev.map(p => (p.id === res.data.id ? res.data : p)));
    } catch (err) {
      alert(err.response?.data?.error || 'فشل إضافة الصورة');
    } finally {
      setAddingExtraImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteExtraImage = async (imageId) => {
    if (!editingSparePart) return;
    if (!window.confirm('حذف هذه الصورة؟')) return;
    try {
      const res = isAccessoriesPage
        ? await adminAPI.deleteAccessoryImage(editingSparePart.id, imageId)
        : await adminAPI.deleteSparePartImage(editingSparePart.id, imageId);
      setEditingSparePart(res.data);
      setSpareParts(prev => prev.map(p => (p.id === res.data.id ? res.data : p)));
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حذف الصورة');
    }
  };

  /** إزالة صورة من قائمة المعاينة قبل الحفظ (وضع الإضافة) */
  const removeExtraImagePreview = (index) => {
    setExtraImagesPreviews((prev) => {
      const next = [...prev];
      if (next[index]) URL.revokeObjectURL(next[index]);
      next.splice(index, 1);
      return next;
    });
    setFormData((prev) => {
      const list = [...(prev.extra_images || [])];
      list.splice(index, 1);
      return { ...prev, extra_images: list };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(formData.variant_ids || []).length) {
      alert('يرجى اختيار سيارة واحدة على الأقل');
      return;
    }
    try {
      const data = new FormData();
      data.append('area', formData.area || 'front');
      data.append('variant_ids', JSON.stringify(Array.isArray(formData.variant_ids) ? formData.variant_ids : []));
      if ((formData.variant_ids || []).length > 0) {
        data.append('variant', formData.variant_ids[0]);
      }
      data.append('name', formData.name);
      data.append('name_ar', formData.name_ar);
      data.append('part_number', formData.part_number || '');
      data.append('quantity', String(formData.quantity ?? 0));
      data.append('price', formData.price || '0');
      data.append('description', formData.description);
      data.append('in_stock', formData.in_stock ? '1' : '0');
      data.append('is_active', formData.is_active ? '1' : '0');
      if (!isAccessoriesPage) data.append('is_accessory', '0');
      if (formData.image) data.append('image', formData.image);
      (formData.extra_images || []).forEach((file) => data.append('extra_images', file));

      if (editingSparePart) {
        if (isAccessoriesPage) {
          await adminAPI.updateAccessory(editingSparePart.id, data);
          alert('تم تحديث الكمالية بنجاح');
        } else {
          await adminAPI.updateSparePart(editingSparePart.id, data);
          alert('تم تحديث قطعة الغيار بنجاح');
        }
      } else {
        if (isAccessoriesPage) {
          await adminAPI.createAccessory(data);
          alert('تم إضافة الكمالية بنجاح');
        } else {
          await adminAPI.createSparePart(data);
          alert('تم إضافة قطعة الغيار بنجاح');
        }
      }

      resetForm();
      fetchSpareParts();
    } catch (err) {
      console.error('Error saving spare part:', err);
      alert(isAccessoriesPage ? 'فشل حفظ الكمالية' : 'فشل حفظ قطعة الغيار');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const applyBarcodeResult = async (code) => {
    if (!code || barcodeProcessedRef.current) return;
    barcodeProcessedRef.current = true;
    setBarcodeLoading(true);
    try {
      const res = isAccessoriesPage
        ? await adminAPI.getAccessoryByBarcode(code)
        : await adminAPI.getSparePartByBarcode(code);
      const item = isAccessoriesPage ? res.data.accessory : res.data.spare_part;
      if (res.data.found && item) {
        setShowBarcodeModal(false);
        setBarcodeInput('');
        setBarcodeMode('manual');
        handleEdit(item);
      } else {
        setShowBarcodeModal(false);
        setBarcodeInput('');
        setBarcodeMode('manual');
        setFormData(prev => ({ ...prev, part_number: code }));
        setEditingSparePart(null);
        setShowForm(true);
      }
    } catch (err) {
      if (err.response?.status === 400 || err.response?.data?.found === false) {
        setShowBarcodeModal(false);
        setBarcodeInput('');
        setBarcodeMode('manual');
        setFormData(prev => ({ ...prev, part_number: code }));
        setEditingSparePart(null);
        setShowForm(true);
      } else {
        alert('فشل البحث بالرمز');
      }
    } finally {
      setBarcodeLoading(false);
      barcodeProcessedRef.current = false;
    }
  };

  const handleBarcodeLookup = async (e) => {
    e.preventDefault();
    const code = barcodeInput.trim();
    if (!code) return;
    await applyBarcodeResult(code);
  };

  const handleEdit = (sparePart) => {
    setEditingSparePart(sparePart);
    const qty = sparePart.quantity ?? 0;
    const ids = Array.isArray(sparePart.variant_ids) ? sparePart.variant_ids : (sparePart.variant ? [sparePart.variant] : []);
    setFormData({
      area: sparePart.area || 'front',
      variant_ids: ids,
      name: sparePart.name || '',
      name_ar: sparePart.name_ar || '',
      part_number: sparePart.part_number || '',
      quantity: qty,
      price: sparePart.price || '',
      description: sparePart.description || '',
      in_stock: qty === 0 ? false : (sparePart.in_stock !== undefined ? sparePart.in_stock : true),
      is_active: sparePart.is_active !== undefined ? sparePart.is_active : true,
      is_accessory: isAccessoriesPage,
      image: null,
    });
    setVariantSearchQuery('');
    setImagePreview(sparePart.image_url || null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isAccessoriesPage ? 'هل أنت متأكد من حذف هذه الكمالية؟' : 'هل أنت متأكد من حذف قطعة الغيار هذه؟')) return;
    try {
      if (isAccessoriesPage) {
        await adminAPI.deleteAccessory(id);
        alert('تم حذف الكمالية بنجاح');
      } else {
        await adminAPI.deleteSparePart(id);
        alert('تم حذف قطعة الغيار بنجاح');
      }
      fetchSpareParts();
    } catch (err) {
      console.error('Error deleting:', err);
      alert(isAccessoriesPage ? 'فشل حذف الكمالية' : 'فشل حذف قطعة الغيار');
    }
  };

  const resetForm = () => {
    setFormData({
      area: 'front',
      variant_ids: [],
      name: '',
      name_ar: '',
      part_number: '',
      quantity: 0,
      price: '',
      description: '',
      in_stock: true,
      is_active: true,
      is_accessory: false,
      image: null,
      extra_images: [],
    });
    setVariantSearchQuery('');
    setVariantDropdownOpen(false);
    setImagePreview(null);
    setExtraImagesPreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setEditingSparePart(null);
    setShowForm(false);
  };

  const handleReorderMove = async (index, direction) => {
    if (direction === 'up' && index <= 0) return;
    if (direction === 'down' && index >= spareParts.length - 1) return;
    const newList = [...spareParts];
    const swap = direction === 'up' ? index - 1 : index + 1;
    [newList[index], newList[swap]] = [newList[swap], newList[index]];
    const ids = newList.map((p) => p.id).filter((id) => id != null);
    if (ids.length === 0) return;
    const prev = spareParts;
    setSpareParts(newList);
    setReorderSaving(true);
    try {
      if (isAccessoriesPage) {
        await adminAPI.reorderAccessories(ids);
      } else {
        await adminAPI.reorderSpareParts(ids);
      }
    } catch (err) {
      setSpareParts(prev);
      alert(err.response?.data?.error || err.response?.data?.detail || err.message || 'فشل تحديث الترتيب');
    } finally {
      setReorderSaving(false);
    }
  };

  const getVariantDisplayName = (v) => (typeof v === 'object' && v !== null ? [v.name, v.model_name].filter(Boolean).join(' - ') : `#${v}`) || `#${v?.id}`;
  const filteredVariants = variantSearchQuery.trim()
    ? variants.filter((v) =>
        getVariantDisplayName(v).toLowerCase().includes(variantSearchQuery.toLowerCase())
      )
    : variants;
  const selectedVariantObjects = (formData.variant_ids || []).map((id) => variants.find((v) => v.id === id)).filter(Boolean);
  const selectVariant = (v) => {
    if ((formData.variant_ids || []).includes(v.id)) return;
    setFormData((prev) => ({ ...prev, variant_ids: [...(prev.variant_ids || []), v.id] }));
    setVariantSearchQuery('');
    setVariantDropdownOpen(false);
  };
  const removeVariant = (id) => {
    setFormData((prev) => ({ ...prev, variant_ids: (prev.variant_ids || []).filter((x) => x !== id) }));
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>{isAccessoriesPage ? 'إدارة الكماليات' : 'إدارة قطع الغيار'}</h1>
        <div className="admin-header-actions">
          <button
            type="button"
            className={`btn ${reorderMode ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setReorderMode((v) => !v);
              setPage(1);
            }}
          >
            {reorderMode ? 'إنهاء وضع الترتيب' : 'ترتيب العرض'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => { setBarcodeInput(''); setShowBarcodeModal(true); }}>
            إضافة بالرمز الشريطي
          </button>
          <button className="btn btn-primary" onClick={() => {
            if (isAccessoriesPage) setFormData((prev) => ({ ...prev, is_accessory: true }));
            setEditingSparePart(null);
            setShowForm(true);
          }}>
            {isAccessoriesPage ? 'إضافة كمالية جديدة' : 'إضافة قطعة غيار جديدة'}
          </button>
        </div>
      </div>

      {reorderMode && (
        <p className="admin-models-note" style={{ marginBottom: '12px' }}>
          يتم تحميل <strong>كل {isAccessoriesPage ? 'الكماليات' : 'قطع الغيار'}</strong> (حتى 2000) لترتيب العرض على الموقع. ألغِ وضع الترتيب للعودة للبحث والصفحات.
        </p>
      )}

      <div className="admin-toolbar" style={{ opacity: reorderMode ? 0.6 : 1, pointerEvents: reorderMode ? 'none' : 'auto' }}>
        <form className="admin-search-form" onSubmit={handleSearchSubmit}>
          <button type="submit" className="btn btn-primary">بحث</button>
          <input
            type="search"
            className="admin-search-input"
            placeholder="بحث (رقم القطعة، الاسم...)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="بحث"
          />
        </form>
        <div className="admin-filters">
          <select
            className="admin-filter-select"
            value={filterArea}
            onChange={(e) => { setFilterArea(e.target.value); setPage(1); }}
            aria-label="فلتر المنطقة"
          >
            <option value="">كل المناطق</option>
            {AREA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="admin-filter-select"
            value={filterVariant}
            onChange={(e) => { setFilterVariant(e.target.value); setPage(1); }}
            aria-label="فلتر السيارة"
          >
            <option value="">كل السيارات</option>
            {variants.map((v) => (
              <option key={v.id} value={v.id}>{v.name} {v.model_name ? `- ${v.model_name}` : ''}</option>
            ))}
          </select>
        </div>
        <div className="admin-page-size">
          <label htmlFor="page-size">عرض:</label>
          <select
            id="page-size"
            className="admin-filter-select"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {showBarcodeModal && (
        <div className="admin-modal-backdrop" onClick={() => { setShowBarcodeModal(false); setBarcodeMode('manual'); setCameraError(null); }}>
          <div className="admin-modal admin-modal-barcode" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>إضافة / بحث بالرمز الشريطي</h2>
              <button type="button" className="admin-modal-close" onClick={() => { setShowBarcodeModal(false); setBarcodeMode('manual'); setCameraError(null); }} aria-label="إغلاق">×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-barcode-tabs">
                <button
                  type="button"
                  className={`admin-barcode-tab ${barcodeMode === 'manual' ? 'active' : ''}`}
                  onClick={() => { setBarcodeMode('manual'); setCameraError(null); }}
                >
                  إدخال يدوي
                </button>
                <button
                  type="button"
                  className={`admin-barcode-tab ${barcodeMode === 'camera' ? 'active' : ''}`}
                  onClick={() => { setBarcodeMode('camera'); setCameraError(null); setLastScannedCode(''); }}
                >
                  مسح بالكاميرا
                </button>
              </div>

              {barcodeMode === 'manual' && (
                <form onSubmit={handleBarcodeLookup}>
                  <p className="admin-barcode-hint">أدخل رقم القطعة. إن وُجدت القطعة تفتح للتعديل، وإلا تفتح نموذج إضافة جديدة مع تعبئة الرقم.</p>
                  <div className="form-group">
                    <input
                      type="text"
                      className="admin-search-input"
                      placeholder="رقم القطعة / الرمز الشريطي"
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={barcodeLoading || !barcodeInput.trim()}>
                      {barcodeLoading ? 'جاري البحث...' : 'بحث / إضافة'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowBarcodeModal(false); setBarcodeMode('manual'); }}>إلغاء</button>
                  </div>
                </form>
              )}

              {barcodeMode === 'camera' && (
                <div className="admin-barcode-camera">
                  <p className="admin-barcode-hint">وجّه الكاميرا نحو الرمز الشريطي. يُقبل الرمز عند قراءته مرتين متتاليتين لتجنّب الأخطاء.</p>
                  {cameraError && <p className="admin-barcode-error">{cameraError}</p>}
                  <div className="admin-barcode-video-wrap" id={BARCODE_SCANNER_ELEMENT_ID} />
                  {lastScannedCode && <p className="admin-barcode-last-code">المقروء: <strong>{lastScannedCode}</strong></p>}
                  {barcodeLoading && <p className="admin-barcode-loading">جاري المعالجة...</p>}
                  <button type="button" className="btn btn-secondary" style={{ marginTop: '0.75rem' }} onClick={() => { setShowBarcodeModal(false); setBarcodeMode('manual'); setCameraError(null); setLastScannedCode(''); }}>إلغاء</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="admin-modal-backdrop" onClick={resetForm}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingSparePart ? (isAccessoriesPage ? 'تعديل الكمالية' : 'تعديل قطعة الغيار') : (isAccessoriesPage ? 'إضافة كمالية جديدة' : 'إضافة قطعة غيار جديدة')}</h2>
              <button type="button" className="admin-modal-close" onClick={resetForm} aria-label="إغلاق">×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-container">
                <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label>المنطقة *</label>
              <select
                name="area"
                value={formData.area || 'front'}
                onChange={handleInputChange}
                required
              >
                {AREA_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group form-group-variant-search">
              <label>السيارات * (يمكن اختيار أكثر من سيارة)</label>
              {selectedVariantObjects.length > 0 && (
                <div className="selected-variants-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {selectedVariantObjects.map((v) => (
                    <span key={v.id} className="variant-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#e8f4fc', borderRadius: '6px', fontSize: '0.9rem' }}>
                      {getVariantDisplayName(v)}
                      <button type="button" onClick={() => removeVariant(v.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} aria-label="إزالة">×</button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                className="admin-search-input"
                placeholder="ابحث وأضف سيارة..."
                value={variantSearchQuery}
                onChange={(e) => {
                  setVariantSearchQuery(e.target.value);
                  setVariantDropdownOpen(true);
                }}
                onFocus={() => setVariantDropdownOpen(true)}
                onBlur={() => setTimeout(() => setVariantDropdownOpen(false), 200)}
                autoComplete="off"
              />
              {variantDropdownOpen && (
                <ul className="admin-variant-dropdown" role="listbox">
                  {filteredVariants.length === 0 ? (
                    <li className="admin-variant-dropdown-empty">لا توجد نتائج</li>
                  ) : (
                    filteredVariants
                      .filter((v) => !(formData.variant_ids || []).includes(v.id))
                      .map((v) => (
                        <li
                          key={v.id}
                          role="option"
                          onMouseDown={(e) => { e.preventDefault(); selectVariant(v); }}
                        >
                          {getVariantDisplayName(v)}
                        </li>
                      ))
                  )}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label>اسم القطعة (عربي) *</label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>اسم القطعة (إنجليزي)</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>رقم القطعة</label>
              <input
                type="text"
                name="part_number"
                value={formData.part_number}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>الكمية</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={(e) => {
                  const q = parseInt(e.target.value, 10) || 0;
                  setFormData((prev) => ({
                    ...prev,
                    quantity: q,
                    in_stock: q === 0 ? false : true,
                  }));
                }}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>السعر</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>الوصف</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="in_stock"
                  checked={formData.in_stock}
                  onChange={handleInputChange}
                />
                متوفر في المخزون
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                نشط
              </label>
            </div>

            <div className="form-group">
              <label>صورة {isAccessoriesPage ? 'الكمالية' : 'القطعة'} (رئيسية)</label>
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleFileChange}
              />
              {(imagePreview || (editingSparePart && editingSparePart.image_url)) && (
                <div className="current-image" style={{ marginTop: '10px' }}>
                  <img
                    src={imagePreview || editingSparePart.image_url}
                    alt="صورة"
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>صور إضافية</label>
              {editingSparePart ? (
                <>
                  <div className="extra-images-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {(editingSparePart.extra_images || []).map((img) => (
                      <div key={img.id} style={{ position: 'relative' }}>
                        <img src={img.image_url} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }} />
                        <button type="button" className="btn btn-sm btn-icon btn-delete" style={{ position: 'absolute', top: 2, left: 2 }} onClick={() => handleDeleteExtraImage(img.id)} title="حذف الصورة" aria-label="حذف الصورة">
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <input type="file" accept="image/*" onChange={handleAddExtraImage} disabled={addingExtraImage} style={{ marginTop: '8px' }} />
                  {addingExtraImage && <span style={{ marginRight: '8px' }}>جاري الرفع...</span>}
                </>
              ) : (
                <>
                  <input
                    ref={extraImagesInputRef}
                    type="file"
                    name="extra_images"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => extraImagesInputRef.current?.click()}
                    style={{ marginBottom: '8px' }}
                  >
                    <i className="fas fa-plus" style={{ marginLeft: '6px' }} />
                    إضافة صورة إضافية
                  </button>
                  {extraImagesPreviews.length > 0 && (
                    <div className="extra-images-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                      {extraImagesPreviews.map((url, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <img src={url} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }} />
                          <button
                            type="button"
                            className="btn btn-sm btn-icon btn-delete"
                            style={{ position: 'absolute', top: 2, left: 2 }}
                            onClick={() => removeExtraImagePreview(i)}
                            title="حذف الصورة"
                            aria-label="حذف الصورة"
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingSparePart ? 'تحديث' : 'إضافة'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                إلغاء
              </button>
            </div>
          </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              {reorderMode && <th>الترتيب</th>}
              <th>الصورة</th>
              <th>المنطقة</th>
              <th>السيارات</th>
              <th>الاسم (عربي)</th>
              <th>الاسم (إنجليزي)</th>
              <th>رقم القطعة</th>
              <th>الكمية</th>
              <th>السعر</th>
              <th>المخزون</th>
              <th>الحالة</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {spareParts.length === 0 && !loading ? (
              <tr className="empty-row">
                <td colSpan={reorderMode ? 12 : 11}>{isAccessoriesPage ? 'لا توجد كماليات' : 'لا توجد قطع غيار'}</td>
              </tr>
            ) : (
              spareParts.map((sparePart, index) => (
                <tr key={sparePart.id} className="admin-table-row">
                  {reorderMode && (
                    <td data-label="الترتيب" className="td-reorder">
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-reorder"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleReorderMove(index, 'up');
                        }}
                        disabled={index === 0 || reorderSaving}
                        title="تحريك لأعلى"
                        aria-label="تحريك لأعلى"
                      >
                        <i className="fas fa-chevron-up" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-reorder"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleReorderMove(index, 'down');
                        }}
                        disabled={index === spareParts.length - 1 || reorderSaving}
                        title="تحريك لأسفل"
                        aria-label="تحريك لأسفل"
                      >
                        <i className="fas fa-chevron-down" />
                      </button>
                      {reorderSaving && <span className="reorder-saving-label">حفظ...</span>}
                    </td>
                  )}
                  <td data-label="الصورة">
                    {(sparePart.image_url || (sparePart.extra_image_urls && sparePart.extra_image_urls[0])) ? (
                      <img src={sparePart.image_url || sparePart.extra_image_urls[0]} alt={sparePart.name} className="table-image" />
                    ) : (
                      <span className="no-image">—</span>
                    )}
                  </td>
                  <td data-label="المنطقة">{AREA_OPTIONS.find((o) => o.value === sparePart.area)?.label || sparePart.area || '—'}</td>
                  <td data-label="السيارات">
                    {sparePart.variants_display && sparePart.variants_display.length > 0
                      ? sparePart.variants_display.map((v) => v.name + (v.model_name ? ` (${v.model_name})` : '')).join('، ')
                      : (sparePart.variant_name || '—')}
                  </td>
                  <td data-label="الاسم (عربي)">{sparePart.name_ar || '—'}</td>
                  <td data-label="الاسم (إنجليزي)">{sparePart.name || '—'}</td>
                  <td data-label="رقم القطعة">{sparePart.part_number || '—'}</td>
                  <td data-label="الكمية">{sparePart.quantity ?? 0}</td>
                  <td data-label="السعر">{sparePart.price ? `${sparePart.price} د.ل` : '—'}</td>
                  <td data-label="المخزون">
                    <span className={`status-badge ${sparePart.in_stock ? 'active' : 'inactive'}`}>
                      {sparePart.in_stock ? 'متوفر' : 'غير متوفر'}
                    </span>
                  </td>
                  <td data-label="الحالة">
                    <span className={`status-badge ${sparePart.is_active ? 'active' : 'inactive'}`}>
                      {sparePart.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                  </td>
                  <td data-label="الإجراءات">
                    <div className="td-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-edit"
                        onClick={() => handleEdit(sparePart)}
                        title="تعديل"
                        aria-label="تعديل"
                        disabled={reorderMode}
                      >
                        <i className="fas fa-pen" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-delete"
                        onClick={() => handleDelete(sparePart.id)}
                        title="حذف"
                        aria-label="حذف"
                        disabled={reorderMode}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="admin-pagination">
          <span className="admin-pagination-info">
            إجمالي {pagination.count} قطعة — صفحة {page} من {pagination.total_pages}
          </span>
          <div className="admin-pagination-buttons">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              disabled={!pagination.previous || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              السابق
            </button>
            <span className="admin-pagination-page">ص {page}</span>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              disabled={!pagination.next || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              التالي
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SparePartsAdmin;
